import { CrlItemStatus } from '../../models';

/**
 * The leading status icon + hover tooltip a CRL item row shows, derived from the
 * item's normalized status. Source of truth: the Client Experience Revamp Figma
 * status reference (node 11158:78211) — see the five states OPEN / FINISHED /
 * MARKED N/A / NEEDS ATTENTION / OVERDUE, each with the tooltip shown on hover.
 *
 * The row used to show a *type* icon (document/questionnaire); the redesign
 * replaces it with a status circle so the list reads as progress:
 *  - `open`      → empty circle (waiting on the client, or N/A),
 *  - `submitted` → filled green check (the client has provided data),
 *  - `attention` → filled red exclamation (needs more info, or overdue).
 *
 * Kept here (not in the template) so the status→icon/tooltip rules have a single
 * source of truth (cxp-state: business rules out of components).
 */
export type CrlStatusIconTone = 'open' | 'submitted' | 'attention';

export interface CrlStatusIcon {
  /** `aiwyn-icon` name. */
  readonly name: string;
  /** Tone modifier that drives the icon color in the row scss. */
  readonly tone: CrlStatusIconTone;
}

const OPEN: CrlStatusIcon = { name: 'circle', tone: 'open' };
const SUBMITTED: CrlStatusIcon = { name: 'circle-check-fill', tone: 'submitted' };
const ATTENTION: CrlStatusIcon = { name: 'circle-exclamation-fill', tone: 'attention' };

const STATUS_ICONS: Record<CrlItemStatus, CrlStatusIcon> = {
  // No client response yet — empty circle.
  [CrlItemStatus.WAITING]: OPEN,
  // N/A reads as resolved-but-empty; the row already strikes the title through.
  [CrlItemStatus.NOT_APPLICABLE]: OPEN,
  // Client has submitted data (in review / finished) — green check.
  [CrlItemStatus.IN_REVIEW]: SUBMITTED,
  [CrlItemStatus.FINISHED]: SUBMITTED,
  // Action required — red exclamation (needs more info, or past due).
  [CrlItemStatus.NEEDS_UPDATE]: ATTENTION,
  [CrlItemStatus.OVERDUE]: ATTENTION,
};

export function getCrlStatusIcon(status: CrlItemStatus | null | undefined): CrlStatusIcon {
  return status ? (STATUS_ICONS[status] ?? OPEN) : OPEN;
}

/**
 * Tooltip shown when hovering the status circle (Figma node 11158:78211). The
 * circle is not interactive, so only states that carry information the row does
 * not already show get a tooltip; the rest return '' and the caller suppresses
 * the bubble. For OVERDUE the copy includes the day count, so pass `daysOverdue`
 * (defaults to a count-less fallback when unknown).
 */
export function getCrlStatusTooltip(status: CrlItemStatus | null | undefined, daysOverdue = 0): string {
  switch (status) {
    case CrlItemStatus.NOT_APPLICABLE:
      return 'Marked as N/A';
    case CrlItemStatus.NEEDS_UPDATE:
      return 'This request needs your attention';
    case CrlItemStatus.OVERDUE:
      return daysOverdue > 0
        ? `This request is overdue by ${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'}`
        : 'This request is overdue';
    default:
      return '';
  }
}
